/**
 * routes.ts - every route PATH in the app, in one place. Pages/links
 * reference `ROUTES.tripDetails(id)` instead of writing `/trips/${id}`
 * inline - if a URL structure ever changes, this is the only file to edit.
 */
export const ROUTES = {
  home: '/',
  searchResults: '/search',
  tripDetails: (tripId: string) => `/trips/${tripId}`,
  routesBrowse: '/routes',
  privateRental: '/rentals',
  quickPay: '/quickpay',

  login: '/login',
  register: '/register',
  changePasswordRequired: '/change-password-required',
  verifyOtp: '/verify-otp',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',

  bookingPassengers: (tripId: string) => `/booking/${tripId}/passengers`,
  bookingReview: (tripId: string) => `/booking/${tripId}/review`,
  payment: (bookingId: string) => `/booking/${bookingId}/payment`,
  bookingConfirmation: (bookingId: string) => `/booking/${bookingId}/confirmation`,

  // Accommodation booking (Phase 3c) - browse a property, hold rooms, reserve a stay
  staySearch: '/stay',
  stayProperty: (propertyId: string) => `/stay/properties/${propertyId}`,
  stayReview: '/stay/review',
  stayPayment: (reservationId: string) => `/stay/reservations/${reservationId}/pay`,
  stayReservationConfirmation: (reservationId: string) => `/stay/reservations/${reservationId}`,
  tripItinerary: (groupReference: string) => `/account/itinerary/${groupReference}`,

  clientDashboard: '/account',
  myTrips: '/account/trips',
  bookingDetails: (bookingId: string) => `/account/trips/${bookingId}`,
  myRentals: '/account/rentals',
  savedPassengers: '/account/passengers',
  wallet: '/account/wallet',
  notifications: '/account/notifications',
  myApplications: '/account/applications',
  myRatings: '/account/ratings',

  // Property portal (Phase 4e) - a property staff member's front desk / folio / housekeeping
  propertyPicker: '/property',
  propertyDashboard: (propertyId: string) => `/property/${propertyId}/dashboard`,
  propertyRooms: (propertyId: string) => `/property/${propertyId}/rooms`,
  propertyPhotos: (propertyId: string) => `/property/${propertyId}/photos`,
  propertyFrontDesk: (propertyId: string) => `/property/${propertyId}/front-desk`,
  propertyCalendar: (propertyId: string) => `/property/${propertyId}/calendar`,
  propertyGuests: (propertyId: string) => `/property/${propertyId}/guests`,
  propertyFolio: (propertyId: string, reservationId: string) =>
    `/property/${propertyId}/front-desk/${reservationId}/folio`,
  propertyHousekeeping: (propertyId: string) => `/property/${propertyId}/housekeeping`,
  propertyInventory: (propertyId: string) => `/property/${propertyId}/inventory`,
  propertyProcurement: (propertyId: string) => `/property/${propertyId}/procurement`,
  propertyMaintenance: (propertyId: string) => `/property/${propertyId}/maintenance`,
  propertyFinance: (propertyId: string) => `/property/${propertyId}/finance`,
  propertyHr: (propertyId: string) => `/property/${propertyId}/hr`,
  propertyPayroll: (propertyId: string) => `/property/${propertyId}/payroll`,
  propertyReports: (propertyId: string) => `/property/${propertyId}/reports`,
  propertyActivity: (propertyId: string) => `/property/${propertyId}/activity`,
  propertyIntegrations: (propertyId: string) => `/property/${propertyId}/integrations`,
  propertySettings: (propertyId: string) => `/property/${propertyId}/settings`,

  partnerDashboard: '/partner',
  partnerVehicles: '/partner/vehicles',
  partnerVehicleDetail: (vehicleId: string) => `/partner/vehicles/${vehicleId}`,
  partnerDrivers: '/partner/drivers',
  partnerDriverDetail: (driverId: string) => `/partner/drivers/${driverId}`,
  partnerTrips: '/partner/trips',
  partnerOfferRide: '/partner/offer-ride',
  partnerTripDetail: (tripId: string) => `/partner/trips/${tripId}`,
  partnerEarnings: '/partner/earnings',
  partnerPayouts: '/partner/payouts',
  partnerPayoutDetail: (payoutId: string) => `/partner/payouts/${payoutId}`,
  partnerReports: '/partner/reports',

  driverDashboard: '/driver',
  driverAssignments: '/driver/assignments',
  driverVehicle: '/driver/vehicle',
  driverOfferCarpool: '/driver/offer-carpool',
  driverRatings: '/driver/ratings',
  driverActivity: '/driver/activity',
  driverQuickPayments: '/driver/quick-payments',
  driverTrip: (tripId: string) => `/driver/trips/${tripId}`,

  agentDashboard: '/agent',
  agentBookTrip: '/agent/book',
  agentBookingPassenger: (tripId: string) => `/agent/book/${tripId}/passenger`,
  agentBookingReview: (tripId: string) => `/agent/book/${tripId}/review`,
  agentPayment: (bookingId: string) => `/agent/book/${bookingId}/payment`,
  agentTicket: (bookingId: string) => `/agent/book/${bookingId}/ticket`,
  agentBookings: '/agent/bookings',
  agentTransactions: '/agent/transactions',
  agentReports: '/agent/reports',

  corporateDashboard: '/corporate',
  corporateProfile: '/corporate/profile',
  corporateDepartments: '/corporate/departments',
  corporateCostCentres: '/corporate/cost-centres',
  corporateEmployees: '/corporate/employees',
  corporateBookings: '/corporate/bookings',
  corporateInvoices: '/corporate/invoices',
  corporateReports: '/corporate/reports',

  adminDashboard: '/admin',
  adminUsers: '/admin/users',
  adminApplications: '/admin/applications',
  adminAgents: '/admin/agents',
  adminCorporateAccounts: '/admin/corporate-accounts',
  adminVerificationPartners: '/admin/verification/partners',
  adminVerificationDrivers: '/admin/verification/drivers',
  adminVerificationVehicles: '/admin/verification/vehicles',
  adminVerificationDocuments: '/admin/verification/documents',
  adminVerificationRura: '/admin/verification/rura',
  adminVerificationCarpoolDrivers: '/admin/verification/carpool-drivers',

  adminPartnerDetail: (partnerId: string) => `/admin/partners/${partnerId}`,
  adminFleet: '/admin/fleet',

  adminTrips: '/admin/trips',
  adminTripDetail: (tripId: string) => `/admin/trips/${tripId}`,
  adminSchedule: '/admin/schedule',
  adminRoutes: '/admin/routes',
  adminLocations: '/admin/locations',
  adminPickupPoints: '/admin/pickup-points',
  adminPaymentsFollowUp: '/admin/payments-followup',
  adminRentals: '/admin/rentals',
  adminReports: '/admin/reports',

  adminHospitalityDashboard: '/admin/hospitality',
  adminHospitalityCommission: '/admin/hospitality/commission',
  adminHospitalitySettlements: '/admin/hospitality/settlements',
  adminHospitalitySubscriptions: '/admin/hospitality/subscriptions',
  adminProperties: '/admin/properties',
  adminPropertyDetail: (propertyId: string) => `/admin/properties/${propertyId}`,

  adminPromotions: '/admin/promotions',
  adminPricingRules: '/admin/pricing-rules',
  adminRideSharingSettings: '/admin/ride-sharing-settings',
  adminRideSharingPricing: '/admin/ride-sharing-pricing',
  adminRatings: '/admin/ratings',
  adminNotificationTemplates: '/admin/notification-templates',
  adminSendNotification: '/admin/notifications/send',
  adminIncidents: '/admin/incidents',

  adminWallets: '/admin/wallets',
  adminWalletDetail: (walletId: string) => `/admin/wallets/${walletId}`,
  adminRefunds: '/admin/refunds',
  adminPayouts: '/admin/payouts',
  adminCommissions: '/admin/commissions',
  adminPayments: '/admin/payments',
  adminQuickPayments: '/admin/quick-payments',

  unauthorized: '/unauthorized',
  notFound: '/not-found',
} as const;
